// @systeme: retours-beta
// ============================================================================
// PairsForm — Dialog « Un retour ? » (phase de construction, FX 2026-08-31)
// ============================================================================
// UN SEUL point d'entrée visuel (retour FX : pas de second bouton flottant,
// « Faire une suggestion » du bouton + existait déjà) : l'entrée SUGGESTION
// du QuickActionsFAB ouvre CE dialog au lieu du formulaire support — pendant
// la phase bêta, les suggestions partent au canal retours_beta, traitées en
// lot avec l'IA (les autres types du + restent des demandes de support qui
// appellent une réponse). L'URL de la page, le titre et le viewport sont
// capturés automatiquement — c'est le contexte qui rend le retour
// exploitable. POST /api/retours-beta, relu sur /admin/retours-beta.
// Fin de bêta : re-pointer l'entrée du + vers le support, le dialog se
// débranche d'une ligne.

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box, Button, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, ToggleButtonGroup, ToggleButton, Chip,
} from "@mui/material";
import { toast } from "sonner";
import { api } from "../lib/api";
import { DialogMobile } from "./ui/DialogMobile";

const TYPES = [
  { value: "OBSERVATION", label: "Observation" },
  { value: "SUGGESTION", label: "Suggestion" },
  { value: "PROBLEME", label: "Problème" },
] as const;

interface RetourBetaDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RetourBetaDialog({ open, onClose }: RetourBetaDialogProps) {
  const location = useLocation();
  const [type, setType] = useState<string>("SUGGESTION");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // URL figée à l'OUVERTURE du dialog : c'est la page que l'utilisateur
  // décrit, même s'il navigue pendant la rédaction.
  const [urlCapturee, setUrlCapturee] = useState("");
  useEffect(() => {
    if (open) setUrlCapturee(location.pathname + location.search);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const envoyer = async () => {
    if (message.trim().length < 3) {
      toast.error("Dites-nous en un peu plus !");
      return;
    }
    setSending(true);
    try {
      await api.post("/retours-beta", {
        type,
        message: message.trim(),
        url: urlCapturee,
        titrePage: document.title || undefined,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      toast.success("Merci ! Votre retour est transmis.");
      onClose();
      setMessage("");
      setType("SUGGESTION");
    } catch {
      toast.error("Envoi impossible — réessayez dans un instant");
    } finally {
      setSending(false);
    }
  };

  return (
    <DialogMobile open={open} onClose={() => !sending && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Un retour sur cette page ?</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: "4px !important" }}>
        <Typography variant="body2" color="text.secondary">
          L'application est en construction : chaque observation, idée ou blocage
          nous aide à l'améliorer. Merci !
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">Page concernée :</Typography>
          <Chip size="small" variant="outlined" label={urlCapturee} sx={{ maxWidth: "100%", fontFamily: "monospace" }} />
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={type}
          onChange={(_, v) => v && setType(v)}
          fullWidth
        >
          {TYPES.map((t) => (
            <ToggleButton key={t.value} value={t.value} sx={{ textTransform: "none" }}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          autoFocus
          multiline
          minRows={4}
          fullWidth
          placeholder="Ce que vous avez observé, ce qui vous a gêné, ce que vous auriez aimé…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Annuler</Button>
        <Button variant="contained" onClick={() => void envoyer()} disabled={sending || message.trim().length < 3}>
          {sending ? "Envoi…" : "Envoyer"}
        </Button>
      </DialogActions>
    </DialogMobile>
  );
}
